import { createSelector } from '@reduxjs/toolkit'
import { getEmployeeDisplayName, type TravellerBudgetLine } from './itemPolicyBudgetUtils'

export interface HotelItemPolicyBudgetView {
  isShown: boolean
  isMultiTraveller: boolean
  perNightPerRoomAmount: number
  perRoomAmount: number
  maxPerRoomAmount: number
  showPerRoomRange: boolean
  totalAmount: number
  maxTotalAmount: number
  currencyCode: string | null
  travellerLines: Array<TravellerBudgetLine>
  numberOfNights: number
  roomCount: number
}

export const selectHotelItemPolicyBudget = createSelector(
  (
    _state: App.State,
    params: {
      cacheKey: string
      numberOfNights: number
      roomCount: number
    },
  ) => params,
  (state: App.State) => state.businessTraveller.itemPolicyEvaluations ?? {},
  (state: App.State) => state.businessTraveller.employees.all ?? {},
  (params, itemPolicyBudgets, employees): HotelItemPolicyBudgetView | undefined => {
    const budget = itemPolicyBudgets[params.cacheKey]
    if (!budget || budget.status !== 'success' || budget.itemPolicyType !== 'HOTEL') {
      return undefined
    }

    const { summary, travellers, currencyCode } = budget

    const travellerLines: Array<TravellerBudgetLine> = travellers.map(traveller => ({
      employeeId: traveller.employeeId,
      displayName: getEmployeeDisplayName(employees, traveller.employeeId),
      perNightPerRoom: traveller.perNightPerRoom,
      perLeg: null,
      perDay: null,
      hasPolicy: traveller.hasPolicy,
      currencyCode: traveller.currencyCode ?? currencyCode,
    }))

    const showPerRoomRange = summary.maxPerRoomAmount !== summary.minPerRoomAmount

    return {
      isShown: summary.isShown,
      isMultiTraveller: summary.isMultiTraveller,
      perNightPerRoomAmount: summary.minNightlyBudgetPerRoom,
      perRoomAmount: summary.minPerRoomAmount,
      maxPerRoomAmount: summary.maxPerRoomAmount,
      showPerRoomRange,
      totalAmount: summary.minTotalAmount,
      maxTotalAmount: summary.maxTotalAmount,
      currencyCode,
      travellerLines,
      numberOfNights: params.numberOfNights,
      roomCount: params.roomCount,
    }
  },
)
